import { useCallback, useEffect, useState } from "react";
import {
  getAdoptionRate,
  getBookContentAnalysis,
  getCampusComparison,
  getCapacity,
  getDashboard,
  getReadingGrowthTrend,
  getReadingMix,
  getReaderCampus,
  getTeacherAdoption,
  getTopStudents,
  getTopTeachers,
} from "shared/services/headOfficeService";
import { CampusDatum, GrowthMetric, KpiDatum, MixDimension, RangeDatum, StudentDatum, TeacherDatum } from "./data";
import {
  AdoptionView,
  AssignmentView,
  ContentView,
  mapAdoption,
  mapAssignmentPipeline,
  mapComparison,
  mapContent,
  mapGrowth,
  mapKpis,
  mapReaderCampuses,
  mapReadingMix,
  mapTeacherAdoption,
  mapTopStudents,
  mapTopTeachers,
} from "./apiMappers";

/* ------------------------------------------------------------------ *
 * Range index → { from, to } window (YYYY-MM-DD).
 * Mirrors the RANGES order in data.ts:
 *   0 This Month · 1 Last 3 Months · 2 Last 6 Months · 3 This Year
 * ------------------------------------------------------------------ */

const pad = (n: number) => String(n).padStart(2, "0");
const iso = (d: Date) =>
  `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

export const rangeToWindow = (rangeIdx: number): { from: string; to: string } => {
  const to = new Date();
  const from = new Date();
  switch (rangeIdx) {
    case 0: // This Month
      from.setDate(1);
      break;
    case 1: // Last 3 Months
      from.setMonth(from.getMonth() - 3);
      break;
    case 3: // This Year
      from.setMonth(0, 1);
      break;
    case 2: // Last 6 Months (default)
    default:
      from.setMonth(from.getMonth() - 6);
      break;
  }
  return { from: iso(from), to: iso(to) };
};

/** unwrap the `{ status, statusCode, message, data }` envelope */
/**
 * Unwrap the `{ status, statusCode, message, data }` envelope.
 *
 * This API answers refusals with HTTP 200 and `status: false` (e.g.
 * `teacher/grade-v1` → "You are not authorized to access this teacher route"),
 * which axios reports as success. Without this check a refused request renders
 * as a legitimately empty widget — indistinguishable from "no data yet".
 * Throwing lets the section's own catch mark it failed instead.
 */
const body = (res: any): any => {
  const payload = res?.data;
  if (payload && payload.status === false) {
    throw new Error(payload.message || "Request was rejected");
  }
  return payload?.data ?? null;
};

export interface CapacityView {
  totalSlots: number;
  filledSlots: number;
  remainingSlots: number;
  filledPercentage: number;
}

export interface HeadOfficeData {
  kpis: KpiDatum[];
  readerCampuses: CampusDatum[];
  readerMaxBooks: number;
  teachers: TeacherDatum[];
  students: StudentDatum[];
  adoption: AdoptionView | null;
  mixDimensions: MixDimension[];
  content: ContentView | null;
  assignment: AssignmentView | null;
  comparison: CampusDatum[];
  /** total campuses the comparison endpoint reports, not just this page */
  comparisonTotal: number;
  teacherAdoption: CampusDatum[];
  growthRange: RangeDatum | null;
  growthMetrics: GrowthMetric[];
  capacity: CapacityView | null;
}

const EMPTY: HeadOfficeData = {
  kpis: [],
  readerCampuses: [],
  readerMaxBooks: 1,
  teachers: [],
  students: [],
  adoption: null,
  mixDimensions: [],
  content: null,
  assignment: null,
  comparison: [],
  comparisonTotal: 0,
  teacherAdoption: [],
  growthRange: null,
  growthMetrics: [],
  capacity: null,
};

/** one loading flag per widget so each skeleton clears on its own API. */
export type HoSection =
  | "kpi"
  | "growth"
  | "mix"
  | "campuses"
  | "adoption"
  | "teachers"
  | "students"
  | "content"
  | "teacherAdoption"
  | "comparison";

export const HO_SECTIONS: HoSection[] = [
  "kpi",
  "growth",
  "mix",
  "campuses",
  "adoption",
  "teachers",
  "students",
  "content",
  "teacherAdoption",
  "comparison",
];

const allLoading = (): Record<HoSection, boolean> =>
  HO_SECTIONS.reduce(
    (a, s) => ({ ...a, [s]: true }),
    {} as Record<HoSection, boolean>,
  );

const MIX_TYPES = "genre,language,age_range,keyword";
const GROWTH_METRICS =
  "books_read,pages_read,cumulative_reading,learning_rate,book_completion_rate,avg_books_per_active_student,engagement";

/**
 * Fetches every head-office endpoint for the selected date range. Each section
 * resolves independently and clears its own `loading` flag as soon as its API
 * responds, so the dashboard reveals widgets one-by-one (each behind its own
 * skeleton) instead of waiting for everything. On a range change the last good
 * data stays on screen and updates in place (no skeleton flash).
 */
export function useHeadOfficeData(range: { from: string; to: string }) {
  const [data, setData] = useState<HeadOfficeData>(EMPTY);
  const [loading, setLoading] = useState<Record<HoSection, boolean>>(allLoading);
  const [error, setError] = useState<string | null>(null);

  const fetchAll = useCallback(() => {
    setError(null);
    // reset every section to loading so the skeletons show again on each fetch
    // (initial load AND whenever the date range changes)
    setLoading(allLoading());
    const win = { from: range.from, to: range.to };

    const done = (sec: HoSection) =>
      setLoading((p) => (p[sec] === false ? p : { ...p, [sec]: false }));

    // track total failures to surface a single error only if EVERYTHING fails
    let settled = 0;
    let failed = 0;
    const total = HO_SECTIONS.length;
    const settleTick = (ok: boolean) => {
      settled += 1;
      if (!ok) failed += 1;
      if (settled === total && failed === total) {
        setError("We couldn't load the head office analytics. Please retry.");
      }
    };

    // run one section: apply its mapped data on success, always clear the flag
    const run = (
      sec: HoSection,
      promise: Promise<any>,
      apply: (payload: any) => void,
    ) =>
      promise
        .then((res) => {
          apply(body(res));
          done(sec);
          settleTick(true);
        })
        .catch(() => {
          done(sec);
          settleTick(false);
        });

    run("kpi", getDashboard(win), (d) =>
      setData((s) => ({ ...s, kpis: mapKpis(d) })),
    );
    run("campuses", getReaderCampus({ ...win, per_page: 4, page: 1 }), (d) => {
      const r = mapReaderCampuses(d);
      setData((s) => ({
        ...s,
        readerCampuses: r.campuses,
        readerMaxBooks: r.maxBooks,
      }));
    });
    run("teachers", getTopTeachers({ ...win, per_page: 4, page: 1 }), (d) =>
      setData((s) => ({ ...s, teachers: mapTopTeachers(d) })),
    );
    run("students", getTopStudents({ ...win, per_page: 4, page: 1 }), (d) =>
      setData((s) => ({ ...s, students: mapTopStudents(d) })),
    );
    run("adoption", getAdoptionRate(win), (d) =>
      setData((s) => ({ ...s, adoption: d ? mapAdoption(d) : null })),
    );
    run("mix", getReadingMix(MIX_TYPES, win), (d) =>
      setData((s) => ({ ...s, mixDimensions: mapReadingMix(d) })),
    );
    run(
      "content",
      // widgets show the top 5; "View All" paginates the rest
      getBookContentAnalysis({ ...win, per_page: 4, page: 1 }),
      (d) =>
        setData((s) => ({
          ...s,
          content: mapContent(d),
          assignment: mapAssignmentPipeline(d),
        })),
    );
    run(
      "comparison",
      getCampusComparison({ ...win, per_page: 4, page: 1 }),
      // keep the pagination total: the widget shows 4 rows and needs to know
      // whether more campuses exist before it can offer "View All"
      (d) =>
        setData((s) => ({
          ...s,
          comparison: mapComparison(d),
          comparisonTotal: Number(d?.pagination?.total) || 0,
        })),
    );
    run(
      "teacherAdoption",
      getTeacherAdoption({ ...win, per_page: 5, page: 1 }),
      (d) => setData((s) => ({ ...s, teacherAdoption: mapTeacherAdoption(d) })),
    );
    run("growth", getReadingGrowthTrend(GROWTH_METRICS, win), (d) => {
      const g = mapGrowth(d);
      setData((s) => ({ ...s, growthRange: g.range, growthMetrics: g.metrics }));
    });

    // capacity isn't a skeleton section (not its own widget); fetch quietly
    getCapacity()
      .then((res) => {
        const cap = body(res);
        setData((s) => ({
          ...s,
          capacity: cap
            ? {
                totalSlots: Number(cap.total_slots) || 0,
                filledSlots: Number(cap.filled_slots) || 0,
                remainingSlots: Number(cap.remaining_slots) || 0,
                filledPercentage: Number(cap.filled_percentage) || 0,
              }
            : null,
        }));
      })
      .catch(() => {});
  }, [range.from, range.to]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  return { data, loading, error, refetch: fetchAll };
}
